import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import '@aws-amplify/ui-react/styles.css';
import { theme } from './theme/theme';
import { DashboardLayout } from './components/DashboardLayout';
import { OverviewPage } from './pages/OverviewPage';
import { AccountsPage } from './pages/AccountsPage';
import { AccountDetailPage } from './pages/AccountDetailPage';
import { MetricsPage } from './pages/MetricsPage';
import { EventsPage } from './pages/EventsPage';
import { SettingsPage } from './pages/SettingsPage';
import { SignupPage } from './pages/SignupPage';
import { LoginPage } from './pages/LoginPage';
import { AdminPage } from './pages/AdminPage';
import { MyAccountPage } from './pages/MyAccountPage';
import { auth } from './services/auth';
import { api } from './services/api';
import { useConfigStore } from './stores/useConfigStore';

/**
 * LogoPulse Dashboard Application
 * Main app component with Amplify authentication and routing
 *
 * Public routes (/signup, /login) render without an auth check.
 * Everything else requires a valid Cognito session.
 */
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ThemeProvider>
  );
}

function AppRoutes() {
  const location = useLocation();

  if (location.pathname === '/signup' || location.pathname === '/login') {
    return (
      <Routes>
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/login" element={<LoginPage />} />
      </Routes>
    );
  }

  return <ProtectedRoutes />;
}

function FullPageSpinner() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
}

/**
 * Protected routes — verifies the Cognito session AND loads the client config
 * before any page is mounted, so child effects never fire with a missing token.
 */
function ProtectedRoutes() {
  const [authState, setAuthState] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');

  useEffect(() => {
    let cancelled = false;
    const initialize = async () => {
      try {
        // fetchAuthSession refreshes silently through the refresh token if needed
        const token = await api.getAuthToken(true);
        if (!token) {
          if (!cancelled) setAuthState('unauthenticated');
          return;
        }
        await useConfigStore.getState().loadConfig();
        if (!cancelled) setAuthState('authenticated');
      } catch {
        if (!cancelled) setAuthState('unauthenticated');
      }
    };
    initialize();
    return () => { cancelled = true; };
  }, []);

  if (authState === 'loading') {
    return <FullPageSpinner />;
  }

  if (authState === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }

  return <AuthenticatedApp />;
}

/**
 * Only mounts the admin page when the cached config says the org is an admin.
 * The backend is still the real gate; this just avoids exposing the route.
 */
function AdminRoute() {
  const role = useConfigStore((s) => s.config?.role);
  if (role !== 'admin') {
    return <Navigate to="/overview" replace />;
  }
  return <AdminPage />;
}

function AuthenticatedApp() {
  const signOut = async () => {
    useConfigStore.getState().clearConfig();
    api.clearAuthToken();
    try { await auth.logout(); } catch { /* ignore */ }
    window.location.href = '/login';
  };

  return (
    <Routes>
      <Route path="/" element={<DashboardLayout signOut={signOut} />}>
        <Route index element={<Navigate to="/overview" replace />} />
        <Route path="overview" element={<OverviewPage />} />
        <Route path="accounts" element={<AccountsPage />} />
        <Route path="accounts/:accountId" element={<AccountDetailPage />} />
        <Route path="metrics" element={<MetricsPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/:tab" element={<SettingsPage />} />
        <Route path="account" element={<MyAccountPage />} />
        <Route path="account/:tab" element={<MyAccountPage />} />
        <Route path="admin" element={<AdminRoute />} />
      </Route>
      <Route path="*" element={<Navigate to="/overview" replace />} />
    </Routes>
  );
}

export default App;
